import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { CheckCircle2, AlertCircle, Info, X } from 'lucide-react'
import { cn } from '@/lib/utils'

const ToastContext = createContext(null)

let seq = 0

/** Provider global de toasts (notificações na parte inferior, centralizadas). */
export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([])

  const dismiss = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
  }, [])

  const push = useCallback((message, opts = {}) => {
    const id = ++seq
    const toast = { id, message, variant: opts.variant || 'success', duration: opts.duration ?? 3500 }
    setToasts((prev) => [...prev, toast])
    return id
  }, [])

  const toast = useMemo(
    () => ({
      success: (msg, o) => push(msg, { ...o, variant: 'success' }),
      error: (msg, o) => push(msg, { ...o, variant: 'error' }),
      info: (msg, o) => push(msg, { ...o, variant: 'info' }),
    }),
    [push]
  )

  return (
    <ToastContext.Provider value={toast}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  )
}

function ToastViewport({ toasts, onDismiss }) {
  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-6 z-[100] flex flex-col items-center gap-2 px-4">
      {toasts.map((t) => (
        <ToastItem key={t.id} toast={t} onDismiss={onDismiss} />
      ))}
    </div>
  )
}

const variants = {
  success: { icon: CheckCircle2, cls: 'border-emerald-500/30 bg-emerald-50 text-emerald-900 dark:bg-emerald-950 dark:text-emerald-100', iconCls: 'text-emerald-600' },
  error: { icon: AlertCircle, cls: 'border-destructive/30 bg-destructive/10 text-destructive', iconCls: 'text-destructive' },
  info: { icon: Info, cls: 'border-border bg-card text-card-foreground', iconCls: 'text-primary' },
}

function ToastItem({ toast, onDismiss }) {
  useEffect(() => {
    if (!toast.duration) return
    const t = setTimeout(() => onDismiss(toast.id), toast.duration)
    return () => clearTimeout(t)
  }, [toast, onDismiss])

  const v = variants[toast.variant] || variants.info
  const Icon = v.icon

  return (
    <div
      className={cn(
        'pointer-events-auto flex items-center gap-3 rounded-lg border px-4 py-3 shadow-lg',
        'min-w-[280px] max-w-md toast-enter',
        v.cls
      )}
      role="status"
    >
      <Icon className={cn('h-5 w-5 shrink-0', v.iconCls)} />
      <span className="text-sm font-medium flex-1">{toast.message}</span>
      <button onClick={() => onDismiss(toast.id)} className="shrink-0 opacity-60 hover:opacity-100">
        <X className="h-4 w-4" />
      </button>
    </div>
  )
}

/** Hook para disparar toasts: `const toast = useToast(); toast.success('Salvo!')`. */
export function useToast() {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error('useToast deve ser usado dentro de <ToastProvider>')
  return ctx
}
